import React, { useCallback, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  Button,
  FeatherLoader,
  getAssessmentCompareRoutePrefix,
  surveyType,
  useErrorService,
  useNotificationService,
} from '@abroad/components'
import { Table, TableInstance } from '../table'
import UserSearch from '../company/UserSearch'
import API from '../../utils/API'
import { formatDate, formatDateTimeWithTimezone } from '../../utils/date'
import { getBrowserTimezone } from '../../utils/timezone'
import reportConstants from '../../constants/reports'

const SESSIONS_SHOWN = 3

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms))

const ASSESSMENT_LABELS = {
  [surveyType.AWARENESS]: 'Awareness Assessment',
  [surveyType.WISDOM]: 'Wisdom Assessment',
  [surveyType.PROSPERITY]: 'True Prosperity Assessment',
}

// column ids double as the API's `sortBy` values; the server sorts the full list
const DEFAULT_SORT = [{ id: 'user', desc: false }]

const columns = [
  {
    id: 'user',
    Header: 'Users',
    width: '22%',
    accessor: (member) => member.fullName,
  },
  {
    id: 'coach',
    Header: 'Coach',
    width: '18%',
    accessor: (member) => member.coaches,
  },
  {
    id: 'session',
    Header: 'Upcoming Scheduled Sessions',
    width: '25%',
    accessor: (member) => member.coaches,
  },
  {
    id: 'assessments',
    Header: 'Assessments Completed',
    width: '22%',
    accessor: (member) => member.assessments,
    disableSortBy: true,
  },
  {
    id: 'assessment-dates',
    Header: 'Assessment Date',
    width: '13%',
    accessor: (member) => member.assessments,
    disableSortBy: true,
  },
]

/**
 * Read-only table of members with their coaches, upcoming sessions and
 * completed assessments for an organization, network or group.
 *
 * @param {string} type   company | network | group
 * @param {string} id     entity id (or team id for group)
 */
const MemberProgress = ({ type, id }) => {
  const Error = useErrorService()
  const Notification = useNotificationService()
  const timezone = getBrowserTimezone()
  const hasMore = useRef(false)
  const latestRequestId = useRef(0)
  const [members, setMembers] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [isExporting, setIsExporting] = useState(false)
  const exportInFlight = useRef(false)
  // "memberId-coachId" keys whose full session list is expanded
  const [expandedSessions, setExpandedSessions] = useState(() => new Set())

  const toggleSessions = (key) =>
    setExpandedSessions((prev) => {
      const next = new Set(prev)
      if (!next.delete(key)) next.add(key)
      return next
    })

  const fetchData = useCallback(
    async ({ search, sortBy, page, limit }) => {
      const requestId = ++latestRequestId.current
      setIsLoading(true)
      try {
        const trimmedSearch = search?.trim()
        const { id: sortById, desc } = sortBy?.[0] || DEFAULT_SORT[0]
        const { data } = await API.AdminAPI.entityService.getMemberProgress(
          type,
          id,
          {
            page,
            limit,
            sortBy: sortById,
            sortOrder: desc ? 'desc' : 'asc',
            ...(trimmedSearch && { search: trimmedSearch }),
          },
        )
        // ignore responses from requests that a newer search has superseded
        if (requestId !== latestRequestId.current) return
        hasMore.current = page < data?.pagination?.totalPages
        setMembers((prev) => {
          if (page === 1) return data.members
          // a member's next session can shift between pages, so skip repeats
          const loadedIds = new Set(prev.map((member) => member._id))
          return [
            ...prev,
            ...data.members.filter((member) => !loadedIds.has(member._id)),
          ]
        })
        setIsLoading(false)
      } catch (e) {
        if (requestId !== latestRequestId.current) return
        hasMore.current = false
        setMembers([])
        setIsLoading(false)
        Error.showError(e)
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [type, id],
  )

  // Returns job status when ready, or null if failed/timed out (toast shown)
  const pollExportJob = async (jobId) => {
    const { REPORT_JOB_STATUS, POLL_INTERVAL_MS, POLL_MAX_ATTEMPTS } =
      reportConstants

    for (let attempt = 0; attempt < POLL_MAX_ATTEMPTS; attempt++) {
      const { data } = await API.AdminAPI.report.getExportStatus(jobId)
      const status = data?.status

      if (status === REPORT_JOB_STATUS.READY) return data

      if (
        status === REPORT_JOB_STATUS.ERROR ||
        status === REPORT_JOB_STATUS.FAILED
      ) {
        Notification.showNotification(
          data?.error === 'NO_DATA'
            ? 'No members matched, so there is nothing to export.'
            : data?.error === 'REPORT_TIMED_OUT'
            ? 'Export timed out. Please try again.'
            : 'Export failed. Please try again.',
          'danger',
        )
        return null
      }

      await sleep(POLL_INTERVAL_MS)
    }

    Notification.showNotification(
      'Export timed out. Please try again.',
      'danger',
    )
    return null
  }

  const handleExport = async (search, sortBy) => {
    if (exportInFlight.current) return
    exportInFlight.current = true
    setIsExporting(true)
    try {
      const trimmedSearch = search?.trim()
      const { id: sortById, desc } = sortBy?.[0] || DEFAULT_SORT[0]
      const { data } = await API.AdminAPI.entityService.exportMemberProgress(
        type,
        id,
        {
          sortBy: sortById,
          sortOrder: desc ? 'desc' : 'asc',
          timezone,
          ...(trimmedSearch && { search: trimmedSearch }),
        },
      )
      const jobId = data?.jobId
      if (!jobId) {
        Notification.showNotification(
          'Export could not be started. Please try again.',
          'danger',
        )
        return
      }

      const jobStatus = await pollExportJob(jobId)
      if (!jobStatus) return

      const { data: blob } = await API.AdminAPI.report.downloadReport(jobId, {
        asBlob: true,
      })
      if (!(blob?.size > 0)) return

      const filename = `${
        jobStatus?.fileName || `${type}_member_progress`
      }.xlsx`
        .replace(/[/\\?%*:|"<>]/g, '')
        .trim()
      const url = window.URL.createObjectURL(blob)
      const downloadLink = document.createElement('a')
      downloadLink.href = url
      downloadLink.download = filename
      downloadLink.style.display = 'none'
      document.body.appendChild(downloadLink)
      downloadLink.click()
      document.body.removeChild(downloadLink)
      window.URL.revokeObjectURL(url)

      Notification.showNotification('Export Successful.', 'success')
    } catch (e) {
      Error.showError(e)
    } finally {
      exportInFlight.current = false
      setIsExporting(false)
    }
  }

  const renderUpcomingSessions = (coach, key) => {
    if (!coach) return '-'

    const sessions = coach.upcomingSessions || []
    if (sessions.length === 0) {
      return <span>No upcoming session</span>
    }

    const isExpanded = expandedSessions.has(key)
    const visibleSessions = isExpanded
      ? sessions
      : sessions.slice(0, SESSIONS_SHOWN)

    return (
      <>
        <div className='font-bold'>Scheduled · {sessions.length}</div>
        {visibleSessions.map((session, index) => {
          const { date, time } = formatDateTimeWithTimezone(
            session.startTime,
            timezone,
          )
          return (
            <div key={`${session.startTime}-${index}`}>
              {date} at {time}
            </div>
          )
        })}
        {sessions.length > SESSIONS_SHOWN && (
          <button
            type='button'
            className='btn btn-link p-0 text-dark-600 shadow-none'
            onClick={() => toggleSessions(key)}>
            {isExpanded
              ? 'Show less'
              : `+${sessions.length - SESSIONS_SHOWN} more`}
          </button>
        )}
      </>
    )
  }

  const renderAssessments = (member) => {
    const assessments = member.assessments || []
    if (assessments.length === 0) {
      return <span>None Taken</span>
    }

    return (
      <>
        {/* <div className='font-semibold text-dark-900'>Yes</div> */}
        {assessments.map((assessment) => (
          <div key={assessment._id}>
            <Link
              to={`/${getAssessmentCompareRoutePrefix(assessment.surveyType)}/${
                assessment._id
              }/clients/${member._id}`}
              target='_blank'>
              {ASSESSMENT_LABELS[assessment.surveyType] ||
                assessment.surveyType}
            </Link>
          </div>
        ))}
      </>
    )
  }

  const renderAssessmentDates = (member) => {
    const assessments = member.assessments || []
    if (assessments.length === 0) return null
    return assessments.map((assessment) => (
      <div key={assessment._id}>{formatDate(assessment.completedAt)}</div>
    ))
  }

  const renderMemberRows = (member) => {
    // one sub-row per coach; member and assessment cells span all of them
    const coachRows = member.coaches?.length ? member.coaches : [null]

    return coachRows.map((coach, index) => {
      const rowKey = `${member._id}-${coach?._id || 'no-coach'}`
      return (
        <tr key={rowKey}>
          {index === 0 && (
            <td rowSpan={coachRows.length}>
              <Link to={`/admin/users/${member._id}`} className='text-dark-600'>
                {member.fullName}
              </Link>
              <div className='s7 text-dark-600'>{member.email}</div>
            </td>
          )}
          <td>
            {coach ? (
              <Link to={`/admin/coaches/${coach._id}`}>{coach.fullName}</Link>
            ) : (
              '-'
            )}
          </td>
          <td>{renderUpcomingSessions(coach, rowKey)}</td>
          {index === 0 && (
            <>
              <td rowSpan={coachRows.length}>{renderAssessments(member)}</td>
              <td rowSpan={coachRows.length}>
                {renderAssessmentDates(member)}
              </td>
            </>
          )}
        </tr>
      )
    })
  }

  const renderBody = (getTableBodyProps, rows) => (
    <tbody {...getTableBodyProps()}>
      {rows.map((row) => (
        <React.Fragment key={row.original._id}>
          {renderMemberRows(row.original)}
        </React.Fragment>
      ))}
      {isLoading && (
        <tr>
          <td colSpan={columns.length} className='text-center'>
            <FeatherLoader />
          </td>
        </tr>
      )}
      {!isLoading && members.length === 0 && (
        <tr>
          <td colSpan={columns.length} className='text-center s6 py-3'>
            No members found.
          </td>
        </tr>
      )}
    </tbody>
  )

  return (
    <TableInstance
      columns={columns}
      data={members}
      disableMultiSort
      initialState={{
        sortBy: DEFAULT_SORT,
        globalFilter: {
          search: '',
        },
      }}>
      {({
        preGlobalFilteredRows,
        state: { globalFilter, ...other },
        setGlobalFilter,
        ...rest
      }) => (
        <>
          <div className='d-flex flex-wrap justify-content-between align-items-center mb-2-5'>
            <div className='col-12 col-lg-4 px-0'>
              <UserSearch
                globalFilter={globalFilter}
                setGlobalFilter={setGlobalFilter}
              />
            </div>
            <Button
              variant='gray'
              className='line-height-normal border-radius-3'
              isLoading={isExporting}
              disabled={isExporting}
              onClick={() => handleExport(globalFilter?.search, other.sortBy)}>
              Export
            </Button>
          </div>
          <Table
            ref={hasMore}
            tableInstance={{
              preGlobalFilteredRows,
              state: { globalFilter, ...other },
              setGlobalFilter,
              ...rest,
            }}
            isLoading={isLoading}
            fetchData={fetchData}
            renderContent={renderBody}
          />
        </>
      )}
    </TableInstance>
  )
}

export default MemberProgress
